import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {Vector2d} from "konva/types/types";
import {RootState} from "../../editorStore";
import {distance, getEditorLine, lineAngle, midpoint} from "../../utils";
import {
	AreaStyle,
	ChangeStyleActionPayload,
	DeleteManyActionPayload,
	EditorLine,
	EditorPoint,
	MaterialChangeActionPayload,
	MaterialMeasurementCreateActionPayload,
	TopViewWallAreaMeasurement,
	TopViewWallAreaMeasurementState,
	WallDeduction,
	WallSegment,
} from "../../models/editor";
import {TopViewWallAreaHelper} from "./TopViewWallAreaHelper";
import {MeasurementType} from "../../models/enums";
import {
	ActivateActionPayload,
	DeleteActionPayload,
	DeleteSelectedItemActionPayload,
	GroupedActionPayload,
	SetVisibilityActionPayload,
} from "../../../../../base-konva/types";
import {getId} from "../../../../../../utils";

const initialState: TopViewWallAreaMeasurementState = {
	topViewWallAreas: []
};

export const topViewWallAreaSlice = createSlice({
	name: 'topViewWallArea',
	initialState,
	reducers: {
		reset: () => ({...initialState}),

		add: (state, {payload}: PayloadAction<MaterialMeasurementCreateActionPayload>) => {
			const id = getId();
			const measurement: TopViewWallAreaMeasurement = {
				version: 1,
				id,
				material: payload.material,
				visible: true,
				style: payload.style as AreaStyle,
				type: MeasurementType.TOP_VIEW_WALL_AREA,
				wallSegments: [],
				activeSegmentId: null,
				heightMap: {},
				wallDeductions: [],
				defaultHeight: payload.defaultHeight,
			};
			state.topViewWallAreas.push(measurement);
			state.activeTopViewWallAreaId = id;
		},

		delete: (state, {payload}: PayloadAction<DeleteActionPayload>) => {
			state.topViewWallAreas = state.topViewWallAreas.filter(m => m.id !== payload.id);
			if (state.activeTopViewWallAreaId === payload.id) {
				state.activeTopViewWallAreaId = undefined;
			}
		},

		activate: (state, {payload}: PayloadAction<ActivateActionPayload>) => {
			state.activeTopViewWallAreaId = payload.id;
		},

		clearActive: (state, _action: PayloadAction<GroupedActionPayload>) => {
			state.activeTopViewWallAreaId = undefined;
		},

		addPoint: (state, {payload}: PayloadAction<Vector2d>) => {
			executeOnActiveMeasurement(state, measurement => {
				const activeSegment = measurement.wallSegments.find(s => s.id === measurement.activeSegmentId);
				if (activeSegment && !activeSegment.closed) {
					addPointToSegment(measurement, activeSegment, payload);
				} else {
					const segmentId = getId();
					const segment: WallSegment = {
						id: segmentId,
						lines: [],
						lastMouseUpPosition: payload,
						closed: false,
					};
					measurement.wallSegments.push(segment);
					measurement.activeSegmentId = segmentId;
				}
			});
		},

		closeWall: (state) => {
			executeOnActiveMeasurement(state, measurement => {
				const activeSegment = measurement.wallSegments.find(s => s.id === measurement.activeSegmentId);
				// Closing forms a polygon by snapping back to the first point: needs ≥2 lines (≥3 points)
				if (activeSegment && !activeSegment.closed && activeSegment.lines.length >= 2) {
					closeSegment(activeSegment);
					measurement.activeSegmentId = null;
				}
			});
		},

		finishSegment: (state) => {
			executeOnActiveMeasurement(state, measurement => {
				const activeSegment = measurement.wallSegments.find(s => s.id === measurement.activeSegmentId);
				if (activeSegment && activeSegment.lines.length >= 1) {
					measurement.activeSegmentId = null;
				}
			});
		},

		closeWalls: (state) => {
			state.topViewWallAreas = state.topViewWallAreas.map(measurement => {
				measurement.wallSegments = measurement.wallSegments
					.filter(s => s.lines.length >= 1)
					.map(segment => {
						// Same invariant as closeWall: only close segments that can form a polygon
						if (!segment.closed && segment.lines.length >= 2) {
							closeSegment(segment);
						}
						return segment;
					});
				measurement.activeSegmentId = null;
				return measurement;
			});
		},

		finishSegments: (state) => {
			state.topViewWallAreas = state.topViewWallAreas.map(measurement => {
				measurement.wallSegments = measurement.wallSegments.filter(s => s.lines.length >= 1);
				measurement.activeSegmentId = null;
				return measurement;
			});
		},

		cancelActiveSegment: (state) => {
			const activeId = state.activeTopViewWallAreaId;
			if (!activeId) return;
			const measurement = state.topViewWallAreas.find(m => m.id === activeId);
			if (!measurement) return;
			const removed = measurement.wallSegments.find(s => s.id === measurement.activeSegmentId);
			if (removed) {
				// Drop heightMap entries for the cancelled segment's points (may have been auto-populated via defaultHeight)
				for (const pointId of collectSegmentPointIds(removed)) {
					delete measurement.heightMap[pointId];
				}
			}
			measurement.wallSegments = measurement.wallSegments.filter(
				s => s.id !== measurement.activeSegmentId
			);
			measurement.activeSegmentId = null;
		},

		updatePointPosition: (state, {payload}: PayloadAction<{segmentId: string, pointId: string, newPosition: Vector2d}>) => {
			const {segmentId, pointId, newPosition} = payload;
			executeOnActiveMeasurement(state, measurement => {
				const segment = measurement.wallSegments.find(s => s.id === segmentId);
				if (segment) {
					updatePointPositionInSegment(segment, pointId, newPosition);
				}
			});
		},

		updatePosition: (state, {payload}: PayloadAction<{positionDelta: Vector2d}>) => {
			const {positionDelta} = payload;
			executeOnActiveMeasurement(state, measurement => {
				measurement.wallSegments = measurement.wallSegments.map(segment => ({
					...segment,
					lines: segment.lines.map(line => {
						const from: EditorPoint = {
							...line.from,
							position: {
								x: line.from.position.x + positionDelta.x,
								y: line.from.position.y + positionDelta.y,
							},
						};
						const to: EditorPoint = {
							...line.to,
							position: {
								x: line.to.position.x + positionDelta.x,
								y: line.to.position.y + positionDelta.y,
							},
						};
						return getEditorLine(line.id, from, to);
					}),
					lastMouseUpPosition: segment.lastMouseUpPosition
						? {
							x: segment.lastMouseUpPosition.x + positionDelta.x,
							y: segment.lastMouseUpPosition.y + positionDelta.y,
						}
						: null,
				}));
			});
		},

		setPointHeight: (state, {payload}: PayloadAction<{id: string, pointId: string, height: number}>) => {
			const measurement = state.topViewWallAreas.find(m => m.id === payload.id);
			if (measurement) {
				measurement.heightMap[payload.pointId] = payload.height;
			}
		},

		removePointHeight: (state, {payload}: PayloadAction<{id: string, pointId: string}>) => {
			const measurement = state.topViewWallAreas.find(m => m.id === payload.id);
			if (measurement) {
				delete measurement.heightMap[payload.pointId];
			}
		},

		setDefaultHeight: (state, {payload}: PayloadAction<{id: string, defaultHeight: number | undefined}>) => {
			const measurement = state.topViewWallAreas.find(m => m.id === payload.id);
			if (measurement) {
				measurement.defaultHeight = payload.defaultHeight;
			}
		},

		changeStyle: (state, {payload}: PayloadAction<ChangeStyleActionPayload<AreaStyle>>) => {
			const measurement = state.topViewWallAreas.find(m => m.id === payload.id);
			if (measurement) {
				measurement.style = {...payload.style};
			}
		},

		changeMaterial: (state, {payload}: PayloadAction<MaterialChangeActionPayload>) => {
			const measurement = state.topViewWallAreas.find(m => m.id === payload.id);
			if (measurement) {
				measurement.material = payload.material;
				measurement.style = payload.style as AreaStyle;
			}
		},

		setVisibility: (state, {payload}: PayloadAction<SetVisibilityActionPayload>) => {
			const {id, visible, setAll} = payload;
			for (const measurement of state.topViewWallAreas) {
				if (measurement.id === id || setAll) {
					measurement.visible = visible;
					if (!setAll) break;
				}
			}
		},

		setTopViewWallAreas: (state, {payload}: PayloadAction<TopViewWallAreaMeasurement[]>) => {
			state.topViewWallAreas = payload;
		},

		addWallDeduction: (state, {payload}: PayloadAction<{id: string, deduction: Omit<WallDeduction, 'id'>}>) => {
			const measurement = state.topViewWallAreas.find(m => m.id === payload.id);
			if (measurement) {
				const deduction = {...payload.deduction, id: getId()};
				if (!isManualAreaDeduction(deduction)) {
					deduction.area = TopViewWallAreaHelper.calculateDeductionArea(deduction);
				}
				measurement.wallDeductions.push(deduction);
			}
		},

		updateWallDeduction: (state, {payload}: PayloadAction<{id: string, deduction: WallDeduction}>) => {
			const measurement = state.topViewWallAreas.find(m => m.id === payload.id);
			if (measurement) {
				const index = measurement.wallDeductions.findIndex(d => d.id === payload.deduction.id);
				if (index !== -1) {
					const deduction = {...payload.deduction};
					if (!isManualAreaDeduction(deduction)) {
						deduction.area = TopViewWallAreaHelper.calculateDeductionArea(deduction);
					}
					measurement.wallDeductions[index] = deduction;
				}
			}
		},

		deleteWallDeduction: (state, {payload}: PayloadAction<{id: string, deductionId: string}>) => {
			const measurement = state.topViewWallAreas.find(m => m.id === payload.id);
			if (measurement) {
				measurement.wallDeductions = measurement.wallDeductions.filter(d => d.id !== payload.deductionId);
			}
		},

		removeSelectionItems: (state, {payload}: PayloadAction<DeleteSelectedItemActionPayload>) => {
			const measurement = state.topViewWallAreas.find(m => m.id === payload.id);
			if (!measurement) return;

			const segmentIndex = measurement.wallSegments.findIndex(s => s.id === payload.fragmentId);
			if (segmentIndex === -1) return;

			const segment = measurement.wallSegments[segmentIndex];

			const closingLineId = `${payload.fragmentId}-closing`;
			const subIds = payload.subIds ?? [];
			const realLineIds = subIds.filter(id => id !== closingLineId);
			const deletingClosingLine = subIds.includes(closingLineId);

			if (segment.closed) {
				// Materialise the closing wall as a real stored line unless it is the one being deleted.
				// This turns "delete line from closed polygon" into "open at the deleted line" rather
				// than silently dropping the closing wall.
				if (!deletingClosingLine && segment.lines.length >= 1) {
					const first = segment.lines[0].from;
					const last = segment.lines[segment.lines.length - 1].to;
					segment.lines.push(getEditorLine(getId(), last, first));
				}
				segment.closed = false;
				segment.lastMouseUpPosition = null;
			}

			// If only the closing line was selected, the segment is now open — nothing more to do.
			if (realLineIds.length === 0) return;

			const remainingLines = segment.lines.filter(line => !realLineIds.includes(line.id));

			if (remainingLines.length === 0) {
				measurement.wallSegments.splice(segmentIndex, 1);
			} else {
				const groups = groupConnectedLines(remainingLines);
				segment.lines = groups[0];
				segment.lastMouseUpPosition = null;
				for (let i = 1; i < groups.length; i++) {
					measurement.wallSegments.push({
						id: getId(),
						lines: groups[i],
						closed: false,
						lastMouseUpPosition: null,
					});
				}
			}

			// Remove heightMap entries for points no longer in any segment
			const referencedPointIds = new Set<string>();
			for (const seg of measurement.wallSegments) {
				for (const line of seg.lines) {
					referencedPointIds.add(line.from.id);
					referencedPointIds.add(line.to.id);
				}
			}
			for (const pointId of Object.keys(measurement.heightMap)) {
				if (!referencedPointIds.has(pointId)) {
					delete measurement.heightMap[pointId];
				}
			}
		},

		removeItems: (state, {payload}: PayloadAction<DeleteManyActionPayload>) => {
			state.topViewWallAreas = state.topViewWallAreas.filter(m => !payload.ids.includes(m.id));
		},
	},
});

function isManualAreaDeduction(d: Pick<WallDeduction, 'leftHeight' | 'rightHeight' | 'topWidth' | 'bottomWidth' | 'area'>): boolean {
	return d.leftHeight === 0 && d.rightHeight === 0 && d.topWidth === 0 && d.bottomWidth === 0 && d.area > 0;
}

function addPointToSegment(measurement: TopViewWallAreaMeasurement, segment: WallSegment, pointPosition: Vector2d) {
	if (segment.closed) return;

	if (segment.lastMouseUpPosition) {
		let lastPoint: EditorPoint = {
			id: getId(),
			position: segment.lastMouseUpPosition,
		};
		if (segment.lines.length > 0) {
			lastPoint = segment.lines[segment.lines.length - 1].to;
		}
		const newPoint: EditorPoint = {
			id: getId(),
			position: pointPosition,
		};
		segment.lastMouseUpPosition = newPoint.position;
		segment.lines.push({
			id: getId(),
			from: lastPoint,
			to: newPoint,
			angle: lineAngle(lastPoint.position, newPoint.position),
			distance: distance(lastPoint.position, newPoint.position),
			center: midpoint(lastPoint.position, newPoint.position),
		});
		if (measurement.defaultHeight !== undefined) {
			measurement.heightMap[newPoint.id] = measurement.defaultHeight;
			// First line of a segment creates two new points; later lines reuse the previous to as from
			if (segment.lines.length === 1) {
				measurement.heightMap[lastPoint.id] = measurement.defaultHeight;
			}
		}
	} else {
		segment.lastMouseUpPosition = pointPosition;
	}
}

function closeSegment(segment: WallSegment) {
	if (segment.lines.length < 1) return;
	segment.closed = true;
}

function updatePointPositionInSegment(segment: WallSegment, pointId: string, newPosition: Vector2d) {
	for (const line of segment.lines) {
		let updated = false;
		if (line.from.id === pointId) {
			line.from.position = newPosition;
			updated = true;
		} else if (line.to.id === pointId) {
			line.to.position = newPosition;
			updated = true;
		}
		if (updated) {
			line.angle = lineAngle(line.from.position, line.to.position);
			line.distance = distance(line.from.position, line.to.position);
			line.center = midpoint(line.from.position, line.to.position);
		}
	}
}

function groupConnectedLines(lines: EditorLine[]): EditorLine[][] {
	if (lines.length === 0) return [];
	// Union-find style: start with each line in its own group, then keep merging
	// groups that share an endpoint until no further merges are possible.
	const groups: EditorLine[][] = lines.map(l => [l]);
	let merged = true;
	while (merged) {
		merged = false;
		outer: for (let i = 0; i < groups.length; i++) {
			for (let j = i + 1; j < groups.length; j++) {
				const connects = groups[i].some(a =>
					groups[j].some(b =>
						a.to.id === b.from.id || a.from.id === b.to.id ||
						a.to.id === b.to.id   || a.from.id === b.from.id
					)
				);
				if (connects) {
					groups[i].push(...groups[j]);
					groups.splice(j, 1);
					merged = true;
					break outer;
				}
			}
		}
	}
	return groups;
}

function collectSegmentPointIds(segment: WallSegment): string[] {
	const ids: string[] = [];
	for (const line of segment.lines) {
		ids.push(line.from.id, line.to.id);
	}
	return ids;
}

function executeOnActiveMeasurement(
	state: TopViewWallAreaMeasurementState,
	callback: (measurement: TopViewWallAreaMeasurement) => void
) {
	const measurement = state.topViewWallAreas.find(m => m.id === state.activeTopViewWallAreaId);
	if (measurement) {
		callback(measurement);
	}
}

export const topViewWallAreaActions = topViewWallAreaSlice.actions;
export const selectTopViewWallAreas = (state: RootState) => state.undoGroup.present.topViewWallArea.topViewWallAreas;
export const selectActiveTopViewWallAreaId = (state: RootState) => state.undoGroup.present.topViewWallArea.activeTopViewWallAreaId;
export const selectIsDrawingWallSegment = (state: RootState) => {
	const wallAreas = state.undoGroup.present.topViewWallArea.topViewWallAreas;
	const activeId = state.undoGroup.present.topViewWallArea.activeTopViewWallAreaId;
	const active = wallAreas.find(m => m.id === activeId);
	if (!active || !active.activeSegmentId) return false;
	const segment = active.wallSegments.find(s => s.id === active.activeSegmentId);
	return Boolean(segment && !segment.closed);
};
export const topViewWallAreaReducer = topViewWallAreaSlice.reducer;
